/**
 * Description Tab Content Component for Structured Product Information
 * 
 * A container component that organizes multiple description sections into
 * a cohesive product information tab. Manages both default CMS sections
 * and additional flexible content sections with consistent styling.
 * 
 * Content Structure:
 * - Default sections (section1-4) with predefined layouts
 * - Additional flexible sections from CMS
 * - Alternating image positions for visual variety
 * - Consistent spacing and typography throughout
 * 
 * Section Management:
 * - Dynamic section rendering based on CMS data
 * - Conditional display of sections with content
 * - Automatic image position alternation
 * - Category-specific fallback handling
 * - Debug logging for development assistance
 * 
 * Layout Features:
 * - Responsive container with max-width constraints
 * - Consistent section spacing and alignment
 * - Professional typography hierarchy
 * - Mobile-optimized layout stacking
 * 
 * CMS Integration:
 * - Sanity CMS data structure handling
 * - Flexible content section support
 * - Error handling for missing data
 * - Graceful degradation for incomplete sections
 * 
 * Performance Considerations:
 * - Efficient conditional rendering
 * - Minimized re-renders with stable references
 * - Component composition for reusability
 * - Optimized debugging in development mode
 * 
 * Accessibility:
 * - Semantic HTML structure for content hierarchy
 * - Screen reader compatible section organization
 * - Consistent heading levels and navigation
 * - Descriptive content structure
 * 
 * @param {object} descriptionTabData - Tab content data from Sanity CMS
 * @param {object} descriptionTabData.defaultSections - Predefined section slots
 * @param {Array} descriptionTabData.sections - Additional flexible sections
 * @param {string} category - Bike category for fallback image selection
 * 
 * @description Organized container for multiple product description sections with CMS integration
 */

import DescriptionSection from './DescriptionSection';
import { useEffect } from 'react';

const DescriptionTabContent = ({ descriptionTabData, category = 'mtb' }) => {
  const { defaultSections = {}, sections = [] } = descriptionTabData || {};
  
  // Debug the data structure
  useEffect(() => {
    console.log(`Description Tab Data for ${category}:`, descriptionTabData);
    console.log('Default Sections:', defaultSections);
    
    if (defaultSections?.section1) {
      console.log(`Section 1 has image:`, !!defaultSections.section1.image);
    }
    if (defaultSections?.section2) {
      console.log('Section 2 Image:', defaultSections.section2.image);
    }
    if (defaultSections?.section3) {
      console.log('Section 3 Image:', defaultSections.section3.image);
    }
    if (defaultSections?.section4) {
      console.log('Section 4 Image:', defaultSections.section4.image);
    }
    
    console.log('Additional Sections:', sections);
  }, [descriptionTabData, defaultSections, sections, category]);
  
  return (
    <div className="description-tab max-w-5xl mx-auto">
      {/* Default sections from Sanity when available */}
      <>
        {/* Section 1 */}
        {defaultSections?.section1 && (
          <DescriptionSection
            title={defaultSections.section1.title || ""}
            description={defaultSections.section1.description || ""}
            image={defaultSections.section1.image || ""}
            imagePosition={defaultSections.section1.imagePosition || "left"}
            category={category}
            index={0}
          />
        )}
        
        {/* Section 2 */}
        {defaultSections?.section2 && (
          <DescriptionSection
            title={defaultSections.section2.title || ""}
            description={defaultSections.section2.description || ""}
            image={defaultSections.section2.image || ""}
            imagePosition={defaultSections.section2.imagePosition || "right"}
            category={category}
            index={1}
          />
        )}
        
        {/* Section 3 */}
        {defaultSections?.section3 && (
          <DescriptionSection
            title={defaultSections.section3.title || ""}
            description={defaultSections.section3.description || ""}
            image={defaultSections.section3.image || ""}
            imagePosition={defaultSections.section3.imagePosition || "left"}
            category={category}
            index={2}
          />
        )}
        
        {/* Section 4 */}
        {defaultSections?.section4 && (
          <DescriptionSection
            title={defaultSections.section4.title || ""}
            description={defaultSections.section4.description || ""}
            image={defaultSections.section4.image || ""}
            imagePosition={defaultSections.section4.imagePosition || "right"}
            category={category}
            index={3}
          />
        )}
      </>
      
      {/* Additional sections from Sanity */}
      {Array.isArray(sections) && sections.map((section, index) => (
        <DescriptionSection
          key={`sanity-section-${index}`}
          title={section.title || `Additional Section ${index + 1}`}
          description={section.description}
          image={section.image}
          imagePosition={section.imagePosition}
          category={category}
          index={index + 4}
        />
      ))}
    </div>
  );
};

export default DescriptionTabContent; 